import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { MapPin, CheckCircle, ArrowRight, Star } from "lucide-react";

const HeroSection = () => {
  const [postcodeVan, setPostcodeVan] = useState("");
  const [postcodeNaar, setPostcodeNaar] = useState("");
  const navigate = useNavigate();

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (postcodeVan.trim()) {
      const params = new URLSearchParams();
      params.set("postcode", postcodeVan.trim());
      if (postcodeNaar.trim()) {
        params.set("postcodeNaar", postcodeNaar.trim());
      }
      navigate(`/quote?${params.toString()}`);
    }
  };

  return (
    <section className="pt-28 pb-16 md:pt-32 md:pb-20 bg-gradient-to-b from-primary/[0.04] to-background">
      <div className="container mx-auto px-4">
        <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-center">
          {/* Left — Text & Form */}
          <div className="text-center lg:text-left">
            <h1 className="text-4xl md:text-5xl lg:text-[3.25rem] font-bold text-foreground mb-4 leading-[1.15]">
              Vergelijk <span className="text-primary">verhuisoffertes</span> en bespaar tot 40%
            </h1>
            
            <p className="text-lg text-muted-foreground mb-8 max-w-xl mx-auto lg:mx-0">
              Ontvang gratis verhuisoffertes van erkende verhuisbedrijven bij jou in de buurt. 
              Vergelijk prijzen en kies de beste deal — 100% vrijblijvend.
            </p>

            {/* Form */}
            <form onSubmit={handleSubmit} className="max-w-xl mx-auto lg:mx-0 mb-5">
              <p className="text-sm font-semibold text-foreground mb-3">Ontvang jouw gratis verhuisoffertes</p>
              <div className="bg-card border border-border rounded-xl p-2 shadow-md">
                <div className="flex flex-col sm:flex-row gap-2">
                  <div className="flex-1 relative">
                    <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-primary" />
                    <Input
                      type="text"
                      placeholder="Postcode of stad van"
                      value={postcodeVan}
                      onChange={(e) => setPostcodeVan(e.target.value)}
                      className="pl-11 h-12 border-0 bg-muted/40 text-base rounded-lg"
                      required
                    />
                  </div>

                  <div className="hidden sm:flex items-center">
                    <ArrowRight className="w-4 h-4 text-muted-foreground" />
                  </div>

                  <div className="flex-1 relative">
                    <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-accent" />
                    <Input
                      type="text"
                      placeholder="Postcode of stad naar"
                      value={postcodeNaar}
                      onChange={(e) => setPostcodeNaar(e.target.value)}
                      className="pl-11 h-12 border-0 bg-muted/40 text-base rounded-lg"
                    />
                  </div>

                  <Button 
                    type="submit" 
                    size="lg" 
                    className="h-12 px-8 bg-accent hover:bg-accent/90 text-accent-foreground font-semibold rounded-lg whitespace-nowrap"
                  >
                    Vergelijk nu
                  </Button>
                </div>
              </div>
            </form>

            {/* Trust stats */}
            <div className="flex flex-wrap items-center justify-center lg:justify-start gap-x-5 gap-y-2 text-sm text-muted-foreground">
              <span className="flex items-center gap-1.5">
                <CheckCircle className="w-4 h-4 text-success" />
                Bespaar tot 40%
              </span>
              <span className="flex items-center gap-1.5">
                <CheckCircle className="w-4 h-4 text-success" />
                Reactie binnen 24 uur
              </span>
              <span className="flex items-center gap-1.5">
                <CheckCircle className="w-4 h-4 text-success" />
                Erkende verhuizers
              </span>
            </div>

            {/* Social proof */}
            <div className="mt-6 flex items-center justify-center lg:justify-start gap-2 text-sm text-muted-foreground">
              <div className="flex gap-0.5">
                {[1,2,3,4,5].map(i => (
                  <Star key={i} className="w-4 h-4 fill-amber-400 text-amber-400" />
                ))}
              </div>
              <span>Al <strong className="text-foreground">15.000+</strong> verhuizingen succesvol geregeld</span>
            </div>
          </div>

          {/* Right — Hero Image */}
          <div className="hidden lg:block">
            <img 
              src="/images/hero-verhuizing.jpg"
              alt="Stel draagt verhuisdozen hun nieuwe woning in"
              className="w-full h-auto rounded-2xl shadow-lg"
              loading="eager"
              fetchPriority="high"
              width={1280}
              height={720}
            />
          </div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
